import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, HelpCircle,
  FileText, Sparkles, Paperclip, X, Link as LinkIcon, StickyNote, Clock, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  confirmAiLink, detachEvidence, getOutcomes, getParserStatus, refreshExif, runParser,
} from '../api/client';
import { EvidencePickerDialog } from './EvidencePickerDialog';
import { AddEvidenceMenu } from './AddEvidenceMenu';
import { AddQuickEvidenceDialog } from './AddQuickEvidenceDialog';

const CATEGORIES = [
  { key: 'property',    label: 'Property' },
  { key: 'zones',       label: 'Zones' },
  { key: 'fabric',      label: 'Fabric' },
  { key: 'hvac',        label: 'HVAC' },
  { key: 'ventilation', label: 'Ventilation' },
  { key: 'hws',         label: 'Hot Water' },
  { key: 'lighting',    label: 'Lighting' },
  { key: 'other',       label: 'Other' },
];

const CONF_STYLE = {
  high:   'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  medium: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  low:    'bg-red-500/10 text-red-300 border-red-500/30',
};

const TYPE_STYLE = {
  assessed_input:    'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  calculated_result: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  reference_limit:   'bg-slate-500/10 text-slate-400 border-slate-500/30',
  administrative:    'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
};

const TYPE_LABEL = {
  assessed_input:    'input',
  calculated_result: 'result',
  reference_limit:   'limit',
  administrative:    'admin',
};

const REQ_STYLE = {
  direct_evidence:              'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  assumption_or_justification:  'bg-amber-500/10 text-amber-300 border-amber-500/30',
  no_evidence_required:         'bg-slate-600/10 text-slate-400 border-slate-600/30',
};

const REQ_LABEL = {
  direct_evidence:              'direct',
  assumption_or_justification:  'assumption',
  no_evidence_required:         'n/a',
};

const REQ_TOOLTIP = {
  direct_evidence:              'DIRECT_EVIDENCE — photograph / site note / plan can support this',
  assumption_or_justification:  'ASSUMPTION_OR_JUSTIFICATION — needs research / manufacturer data / defensible-default note',
  no_evidence_required:         'NO_EVIDENCE_REQUIRED — software / regulatory setting; Stage 4 AI skips this',
};

const STATUS_STYLE = {
  unmatched:      'text-red-400',
  needs_review:   'text-amber-400',
  supported:      'text-emerald-400',
  partial:        'text-amber-300',
  assumption:     'text-cyan-300',
  no_support:     'text-red-400',
  conflict:       'text-red-500',
};

const STATUS_LABEL = {
  unmatched:      'No support',
  needs_review:   'Needs review',
  supported:      'Supported',
  partial:        'Partial',
  assumption:     'Assumption',
  no_support:     'No support',
  conflict:       'Conflict',
};

const FILE_STATUS_STYLE = {
  full:               'text-emerald-400',
  partial:            'text-amber-400',
  skipped_no_outcomes:'text-cyan-400',
  unrecognised:       'text-red-400',
};

const FILE_STATUS_LABEL = {
  full: 'Full parse',
  partial: 'Partial parse',
  skipped_no_outcomes: 'Deliberately skipped',
  unrecognised: 'Not recognised',
};

const fmtValue = (o) => {
  if (o.value === null || o.value === undefined || o.value === '') return '—';
  if (typeof o.value === 'number') {
    const rounded = Math.abs(o.value) < 1 ? o.value.toFixed(3) : o.value.toFixed(2);
    return `${rounded}${o.unit ? ' ' + o.unit : ''}`;
  }
  return `${o.value}${o.unit ? ' ' + o.unit : ''}`;
};

const fmtCapture = (iso, tz) => {
  if (!iso) return null;
  // Preserve genuineness: parse as-is, format with seconds. If tz is provided
  // we surface it separately so the assessor sees the camera's local time,
  // not a browser-local reinterpretation.
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const s = d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return tz ? `${s} ${tz}` : s;
};

const CAPTURE_UNAVAILABLE = 'Original capture time unavailable';

const captureLine = (file) => {
  if (!file) return null;
  const cap = fmtCapture(file.capture_datetime, file.capture_timezone);
  return cap || CAPTURE_UNAVAILABLE;
};

const KIND_CHIP_ICON = {
  photo: Paperclip,
  site_plan: Paperclip,
  site_note: StickyNote,
  research: LinkIcon,
  technical_doc: FileText,
  client_doc: FileText,
  unknown: Paperclip,
};

const EvidenceChip = ({ link, outcomeId, onRemove, onConfirm }) => {
  const file = link.file || {};
  const Icon = KIND_CHIP_ICON[file.kind] || Paperclip;
  const isAi = link.link_type === 'ai_suggested';
  const isResearch = file.kind === 'research' || file.kind === 'technical_doc';
  const isNote = file.kind === 'site_note';
  const isPhoto = file.kind === 'photo' || file.kind === 'site_plan';
  const cap = isPhoto ? captureLine(file) : null;
  const titleAttr = [
    file.original_filename,
    isResearch && file.research_meta?.url,
    isResearch && file.research_meta?.title,
    isNote && file.assumption_meta?.reason,
    link.note,
    isPhoto && cap ? `Captured ${cap}` : null,
    isAi ? `AI suggestion (not confirmed): ${link.what_is_visible || ''}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono rounded px-1.5 py-0.5 mr-1 mb-1 border ${isAi ? 'bg-amber-500/10 border-amber-500/40 border-dashed' : 'bg-slate-800 border-slate-700'}`}
      data-testid={`link-${outcomeId}-${file.reference || link.file_id}`}
      title={titleAttr}
    >
      <Icon className={`w-2.5 h-2.5 ${isAi ? 'text-amber-400' : 'text-emerald-400'}`} />
      <span className={`${isAi ? 'text-amber-300' : 'text-emerald-400'} font-semibold`}>{file.reference || '?'}</span>
      {isAi && <span className="text-amber-400/80 uppercase tracking-wider">AI</span>}
      {isPhoto && (
        <span className="text-slate-500" data-testid={`link-capture-${outcomeId}-${file.reference}`}>
          {cap ? cap.split(' ').slice(0, 4).join(' ') : CAPTURE_UNAVAILABLE}
        </span>
      )}
      {isResearch && file.research_meta?.url && (
        <a
          href={file.research_meta.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-cyan-300 hover:text-cyan-200 underline decoration-dotted max-w-[160px] truncate"
        >
          {file.research_meta.title || file.research_meta.url}
        </a>
      )}
      {link.note && <span className="text-slate-400 italic truncate max-w-[120px]">“{link.note}”</span>}
      {isAi && (
        <button
          type="button"
          onClick={() => onConfirm(link.id)}
          className="text-amber-300 hover:text-emerald-400 ml-0.5"
          aria-label="Confirm AI suggestion"
          title="Confirm — this photo supports the input"
          data-testid={`confirm-link-${outcomeId}-${link.id}`}
        >
          <Check className="w-2.5 h-2.5" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onRemove(link.id)}
        className="text-slate-500 hover:text-red-400 ml-0.5"
        aria-label="Remove link"
        data-testid={`unlink-${outcomeId}-${link.id}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </span>
  );
};
// (Lucide X is imported above with the other icons.)

export const OutcomesPanel = ({ projectId, filesRefreshKey }) => {
  const [status, setStatus] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [category, setCategory] = useState(null);
  const [evidenceRelevantOnly, setEvidenceRelevantOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState('research');
  const [activeOutcome, setActiveOutcome] = useState(null);
  const [refreshingExif, setRefreshingExif] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        getParserStatus(projectId),
        getOutcomes(projectId, { category, evidenceRelevantOnly }),
      ]);
      setStatus(s);
      setOutcomes(o);
    } catch (e) {
      toast.error('Failed to load parser status');
    } finally {
      setLoading(false);
    }
  }, [projectId, category, evidenceRelevantOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  const runParse = async () => {
    setRunning(true);
    try {
      const result = await runParser(projectId);
      toast.success(`Parser complete — ${result.outcomes_extracted} outcomes from ${result.files_processed} files`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Parser failed');
    } finally {
      setRunning(false);
    }
  };

  const openPicker = (outcome) => {
    setActiveOutcome(outcome);
    setPickerOpen(true);
  };

  const openQuickAdd = (outcome, mode) => {
    setActiveOutcome(outcome);
    setQuickAddMode(mode);
    setQuickAddOpen(true);
  };

  const runExifRefresh = async () => {
    setRefreshingExif(true);
    try {
      const r = await refreshExif(projectId);
      if (r.updated > 0) {
        toast.success(`Recovered genuine EXIF timestamps on ${r.updated} of ${r.scanned} photos.`);
      } else {
        toast.info(`Scanned ${r.scanned} photos — no additional EXIF timestamps found.`);
      }
      await refresh();
    } catch (e) {
      toast.error('EXIF refresh failed');
    } finally {
      setRefreshingExif(false);
    }
  };

  const handleRemoveLink = async (outcome, linkId) => {
    try {
      await detachEvidence(outcome.id, linkId);
      toast.success('Evidence link removed');
      refresh();
    } catch {
      toast.error('Failed to remove link');
    }
  };

  const handleConfirmLink = async (outcome, linkId) => {
    try {
      await confirmAiLink(outcome.id, linkId);
      toast.success('AI suggestion confirmed');
      refresh();
    } catch {
      toast.error('Failed to confirm link');
    }
  };

  const total = status?.outcomes_total ?? 0;
  const evidenceRelevant = status?.evidence_relevant_total ?? 0;
  const perFile = status?.per_file ?? [];
  const byConf = status?.by_confidence ?? {};
  const byCat = status?.by_category ?? {};
  const byType = status?.by_outcome_type ?? {};
  const byStatus = status?.by_evidence_status ?? {};
  const byReq = status?.by_evidence_requirement ?? {};
  const aiMatchable = status?.ai_matchable_total ?? 0;
  const conflicts = status?.result_conflicts ?? 0;
  const unrecognised = status?.unrecognised_files ?? 0;

  return (
    <section data-testid="outcomes-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
            Assessment outcomes
          </h2>
          <span className="text-xs text-slate-500">
            (extracted from result files — <em>not evidence</em>)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-700 overflow-hidden text-[11px]">
            <button
              type="button"
              onClick={() => setEvidenceRelevantOnly(true)}
              data-testid="view-evidence-relevant"
              className={`px-2.5 py-1 ${evidenceRelevantOnly ? 'bg-emerald-500 text-slate-950 font-medium' : 'bg-slate-900 text-slate-300'}`}
            >
              Evidence-relevant
            </button>
            <button
              type="button"
              onClick={() => setEvidenceRelevantOnly(false)}
              data-testid="view-all"
              className={`px-2.5 py-1 border-l border-slate-700 ${!evidenceRelevantOnly ? 'bg-emerald-500 text-slate-950 font-medium' : 'bg-slate-900 text-slate-300'}`}
            >
              All extracted
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={runExifRefresh} disabled={refreshingExif} data-testid="refresh-exif-btn" className="text-slate-400 hover:text-slate-100">
            <Clock className={`w-3.5 h-3.5 mr-1 ${refreshingExif ? 'animate-spin' : ''}`} />
            {refreshingExif ? 'Reading EXIF…' : 'Refresh EXIF'}
          </Button>
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading || running} data-testid="outcomes-refresh" className="text-slate-400 hover:text-slate-100">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={runParse} disabled={running} data-testid="run-parser-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-medium">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
            {running ? 'Parsing…' : 'Run parser'}
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Evidence-relevant</div>
          <div className="text-2xl font-semibold text-emerald-400 tabular-nums" data-testid="evidence-relevant-total">{evidenceRelevant}</div>
          <div className="text-[10px] text-slate-500">assessed inputs</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Calculated</div>
          <div className="text-2xl font-semibold text-slate-300 tabular-nums">{byType.calculated_result ?? 0}</div>
          <div className="text-[10px] text-slate-500">BER, BPER, ratings</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Reference limits</div>
          <div className="text-2xl font-semibold text-slate-400 tabular-nums">{byType.reference_limit ?? 0}</div>
          <div className="text-[10px] text-slate-500">Part L, TER, TPER</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Administrative</div>
          <div className="text-2xl font-semibold text-cyan-300 tabular-nums">{byType.administrative ?? 0}</div>
          <div className="text-[10px] text-slate-500">address, cert refs</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Supported</div>
          <div className="text-2xl font-semibold text-emerald-300 tabular-nums" data-testid="supported-count">{byStatus.supported ?? 0}</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Conflicts</div>
          <div className={`text-2xl font-semibold tabular-nums ${conflicts > 0 ? 'text-red-400' : 'text-slate-400'}`}>{conflicts}</div>
        </div>
      </div>

      {/* Evidence-requirement breakdown (Stage 4 targeting) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-slate-900 border border-emerald-500/30 rounded-lg p-3" title={REQ_TOOLTIP.direct_evidence}>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Direct evidence</div>
          <div className="text-2xl font-semibold text-emerald-300 tabular-nums" data-testid="req-direct-count">{byReq.direct_evidence ?? 0}</div>
          <div className="text-[10px] text-slate-500">photo / note / plan supports it</div>
        </div>
        <div className="bg-slate-900 border border-amber-500/30 rounded-lg p-3" title={REQ_TOOLTIP.assumption_or_justification}>
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Assumption / justification</div>
          <div className="text-2xl font-semibold text-amber-300 tabular-nums" data-testid="req-assumption-count">{byReq.assumption_or_justification ?? 0}</div>
          <div className="text-[10px] text-slate-500">needs research / defensible default</div>
        </div>
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3" title={REQ_TOOLTIP.no_evidence_required}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">No evidence required</div>
          <div className="text-2xl font-semibold text-slate-400 tabular-nums" data-testid="req-none-count">{byReq.no_evidence_required ?? 0}</div>
          <div className="text-[10px] text-slate-500">software / regulator setting</div>
        </div>
        <div className="bg-slate-900 border border-cyan-500/30 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-cyan-400">AI-matchable target</div>
          <div className="text-2xl font-semibold text-cyan-300 tabular-nums" data-testid="ai-matchable-count">{aiMatchable}</div>
          <div className="text-[10px] text-slate-500">Stage 4 will attempt these</div>
        </div>
      </div>

      {/* Per-file parse status */}
      {perFile.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Per-file parse status</div>
          <ul className="divide-y divide-slate-800 text-xs">
            {perFile.map((f) => (
              <li key={f.file_id} className="flex items-center justify-between py-1.5" data-testid={`parse-file-${f.file_reference}`}>
                <span className="text-slate-200 font-mono">{f.file_reference}</span>
                <span className="text-slate-400 flex-1 px-3 truncate">{f.kind_label}</span>
                <span className={`font-medium ${FILE_STATUS_STYLE[f.status] || 'text-slate-400'}`}>
                  {FILE_STATUS_LABEL[f.status] || f.status}
                </span>
                <span className="text-slate-500 font-mono ml-3 w-12 text-right">{f.outcome_count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Category filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => setCategory(null)} data-testid="filter-cat-all" className={`text-xs px-2.5 py-1 rounded-full border ${category === null ? 'bg-slate-100 text-slate-950 border-slate-100' : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'}`}>
          All ({outcomes.length})
        </button>
        {CATEGORIES.map((c) => {
          const n = byCat[c.key] ?? 0;
          return (
            <button key={c.key} type="button" onClick={() => setCategory(c.key)} disabled={n === 0} data-testid={`filter-cat-${c.key}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                category === c.key ? 'bg-slate-100 text-slate-950 border-slate-100'
                : n === 0 ? 'bg-slate-900 text-slate-600 border-slate-800 cursor-not-allowed'
                : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'
              }`}
            >
              {c.label} ({n})
            </button>
          );
        })}
      </div>

      {/* Outcomes table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
      ) : outcomes.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 border-dashed rounded-lg py-10 text-center text-slate-400">
          <HelpCircle className="w-6 h-6 mx-auto mb-2 text-slate-600" />
          {total === 0
            ? 'No outcomes yet — click "Run parser" to extract from your assessment result files.'
            : evidenceRelevantOnly
              ? 'No assessed inputs found in the current parse. Try "All extracted" or upload the detailed iSBEM export.'
              : 'No outcomes in this category.'}
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="outcomes-table">
            <thead className="bg-slate-950 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-2 pl-3">Category</th>
                <th className="text-left p-2">Outcome</th>
                <th className="text-left p-2">Scope</th>
                <th className="text-right p-2">Value</th>
                <th className="text-center p-2">Type</th>
                <th className="text-center p-2">Conf.</th>
                <th className="text-left p-2">Evidence</th>
                <th className="text-center p-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {outcomes.map((o) => {
                const links = o.links || [];
                const isInput = o.outcome_type === 'assessed_input';
                return (
                  <tr key={o.id} className={o.result_conflict ? 'bg-red-900/10' : ''} data-testid={`outcome-row-${o.id}`}>
                    <td className="p-2 pl-3 text-slate-400 text-xs capitalize">{o.category}</td>
                    <td className="p-2 text-slate-200">{o.label}</td>
                    <td className="p-2 text-slate-500 text-xs font-mono truncate max-w-[200px]">{o.ref_scope || '—'}</td>
                    <td className="p-2 text-right font-mono text-slate-100">{fmtValue(o)}</td>
                    <td className="p-2 text-center">
                      <span className={`text-[10px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${TYPE_STYLE[o.outcome_type] || TYPE_STYLE.assessed_input}`}>
                        {TYPE_LABEL[o.outcome_type] || o.outcome_type}
                      </span>
                      {isInput && o.evidence_requirement && (
                        <span
                          className={`ml-1 text-[10px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${REQ_STYLE[o.evidence_requirement] || ''}`}
                          title={REQ_TOOLTIP[o.evidence_requirement] || ''}
                          data-testid={`req-${o.id}`}
                        >
                          {REQ_LABEL[o.evidence_requirement] || o.evidence_requirement}
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`text-[10px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${CONF_STYLE[o.confidence]}`}>
                        {o.confidence}
                      </span>
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-0.5">
                        {links.length === 0 ? (
                          <span className={`text-[10px] uppercase tracking-widest font-medium ${STATUS_STYLE[o.evidence_status] || 'text-slate-400'}`}>
                            {isInput ? (STATUS_LABEL[o.evidence_status] || o.evidence_status) : '—'}
                          </span>
                        ) : (
                          links.map((l) => (
                            <EvidenceChip key={l.id} link={l} outcomeId={o.id} onRemove={(lid) => handleRemoveLink(o, lid)} onConfirm={(lid) => handleConfirmLink(o, lid)} />
                          ))
                        )}
                        {o.result_conflict && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-red-400 ml-1">
                            <AlertTriangle className="w-3 h-3" /> conflict
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 pr-3 text-center">
                      {isInput ? (
                        <AddEvidenceMenu
                          outcomeId={o.id}
                          onPickPhoto={() => openPicker(o)}
                          onOpenQuickAdd={(mode) => openQuickAdd(o, mode)}
                        />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-slate-700 inline" title="Not evidence-relevant" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed">
        <strong className="text-slate-400">Note.</strong> Only <em>assessed-input</em> rows normally
        need evidence. Regulatory limits (Part L, TER, TPER) and calculated results
        (BER, BPER, EPC rating) are hidden by default — switch to "All extracted" to inspect them.
        Assessment result files never appear as evidence, only as sources.
      </p>

      <EvidencePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        projectId={projectId}
        outcome={activeOutcome}
        onAttached={() => refresh()}
      />

      <AddQuickEvidenceDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        projectId={projectId}
        outcome={activeOutcome}
        mode={quickAddMode}
        onAdded={() => refresh()}
      />
    </section>
  );
};
